import React, { useEffect, useState } from "react";
import type { Firestore } from "firebase/firestore";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  increment,
  getDocs,
  onSnapshot,
  orderBy,
  serverTimestamp,
  updateDoc,
  query,
  limit,
  where,
} from "firebase/firestore";
import { fmtDateOnly } from "../lib/formatters";
import type { AppUser } from "../hooks/useAuthState";
import { createPortal } from "react-dom";
import BatchDetail from "./BatchDetail";

type Props = {
  db: Firestore;
  user: AppUser | null;
  authReady: boolean;
  isCustomerPortal: boolean;
  onOpenItem?: (id: string) => void;
};

type Batch = {
  id: string;
  name: string;
  kolliCount?: number;
  createdAt?: any;
  createdBy?: string | null;
  status?: string;
};

const STATUS_LABELS: Record<string, string> = {
  open: "Öppen",
  ready_to_ship: "Färdig att skicka",
  sold: "Såld",
  shipped: "Skickad",
  invoiced: "Fakturerad",
  paid: "Betalning mottagen",
  credited: "Kredit",
};

const STATUS_ORDER = ["open", "ready_to_ship", "sold", "shipped", "invoiced", "paid", "credited"] as const;

const labelForStatus = (status?: string): string => {
  const key = String(status || "open");
  return STATUS_LABELS[key] || key;
};

export default function VidareforsaljningPage({
  db,
  user,
  authReady,
  isCustomerPortal,
  onOpenItem,
}: Props): JSX.Element | null {
  const [salesBatches, setSalesBatches] = useState<Batch[]>([]);
  const [newBatchName, setNewBatchName] = useState<string>("");
  const [openedBatchId, setOpenedBatchId] = useState<string | null>(null);
  const [activeStatus, setActiveStatus] = useState<string>("all");

  useEffect(() => {
    if (!authReady || !user || isCustomerPortal) return;
    const q = query(collection(db, "salesBatches"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as Batch[];
      setSalesBatches(rows);
    });
    return () => unsub();
  }, [authReady, db, isCustomerPortal, user]);

  const handleCreateSalesBatch = async (): Promise<void> => {
    const name = (newBatchName || "").trim();
    if (!name) return;
    try {
      const batchRef = await addDoc(collection(db, "salesBatches"), {
        name,
        kolliCount: 1,
        status: "open",
        createdAt: serverTimestamp(),
        createdBy: user?.uid || null,
      });
      await addDoc(collection(db, "salesBatches", batchRef.id, "kollin"), {
        name: "Kolli 1",
        createdAt: serverTimestamp(),
        createdBy: user?.uid || null,
      });
      setNewBatchName("");
    } catch (e: any) {
      alert("Kunde inte skapa batch: " + (e?.message || e));
    }
  };

  const handleAddKolli = async (batchId: string): Promise<void> => {
    try {
      // Beräkna nästa kolli-namn: Kolli (kolliCount + 1)
      const batchSnap = await getDocs(query(collection(db, "salesBatches"), where("__name__", "==", batchId)));
      const current = batchSnap.docs[0]?.data() as any;
      const nextIndex = Number(current?.kolliCount || 1) + 1;
      const nextName = `Kolli ${nextIndex}`;

      await addDoc(collection(db, "salesBatches", batchId, "kollin"), {
        name: nextName,
        createdAt: serverTimestamp(),
        createdBy: user?.uid || null,
      });
      await updateDoc(doc(db, "salesBatches", batchId), {
        kolliCount: increment(1),
      });
    } catch (e: any) {
      alert("Kunde inte skapa kolli: " + (e?.message || e));
    }
  };

  const goToBatch = (id: string) => {
    setOpenedBatchId(id);
  };

  const closeBatch = () => setOpenedBatchId(null);

  const handleOpenItemFromBatch = (id: string) => {
    if (!id) return;
    if (onOpenItem) {
      onOpenItem(id);
      // Stäng batchmodalen efter att vi triggat edit-modal
      // setTimeout(closeBatch, 0); // Borttagen för att hålla batch-detalj öppen
    } else {
      closeBatch();
    }
  };

  const handleDeleteBatch = async (batchId: string): Promise<void> => {
    if (!batchId) return;
    try {
      const itemsSnap = await getDocs(query(collection(db, "itInventory"), where("salesBatchId", "==", batchId), limit(1)));
      if (!itemsSnap.empty) {
        alert("Batchen innehåller enheter. Flytta dem till annan batch innan du tar bort.");
        return;
      }
      const ok = window.confirm("Ta bort batchen? Detta går inte att ångra.");
      if (!ok) return;

      const kolliSnap = await getDocs(collection(db, "salesBatches", batchId, "kollin"));
      const deletes: Promise<any>[] = [];
      kolliSnap.docs.forEach((d) => {
        deletes.push(deleteDoc(doc(db, "salesBatches", batchId, "kollin", d.id)));
      });
      await Promise.all(deletes);
      await deleteDoc(doc(db, "salesBatches", batchId));
    } catch (e: any) {
      alert("Kunde inte ta bort batch: " + (e?.message || e));
    }
  };

  if (isCustomerPortal) return null;

  const filteredBatches = activeStatus === "all"
    ? salesBatches
    : salesBatches.filter((b) => String(b.status || "open") === activeStatus);

  const statusCounts = salesBatches.reduce<Record<string, number>>((acc, b) => {
    const key = String(b.status || "open");
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  return (
    <section className="w-full max-w-[1400px] mx-auto mt-6 px-4">
      <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h2 className="gw-h3 mb-1">Vidareförsäljning</h2>
          <p className="text-sm text-muted">Skapa försäljningsbatcher och lägg till kollin.</p>
        </div>
        <div className="flex gap-2 w-full md:w-auto items-center">
          <input
            type="text"
            placeholder="Namn på batch"
            value={newBatchName}
            onChange={(e) => setNewBatchName(e.target.value)}
            className="gw-input min-w-[220px]"
          />
          <button
            type="button"
            className="gw-btn gw-btn-primary whitespace-nowrap shrink-0"
            onClick={handleCreateSalesBatch}
            disabled={!newBatchName.trim()}
          >
            Skapa batch
          </button>
        </div>
      </header>

      <div className="flex flex-wrap gap-2 mb-4">
        {STATUS_ORDER.map((s) => (
          <button
            key={s}
            type="button"
            className={`gw-pill ${activeStatus === s ? "is-selected" : ""}`}
            onClick={() => setActiveStatus(s)}
          >
            {STATUS_LABELS[s] || s} ({statusCounts[s] || 0})
          </button>
        ))}
        <button
          type="button"
          className={`gw-pill ${activeStatus === "all" ? "is-selected" : ""}`}
          onClick={() => setActiveStatus("all")}
        >
          Alla ({salesBatches.length})
        </button>
      </div>

      <div className="gw-table-wrap">
        <table>
          <thead>
            <tr>
              <th>Namn</th>
              <th className="w-24">Kollin</th>
              <th className="w-36">Skapad</th>
              <th className="w-44">Skapad av</th>
              <th className="w-44">Status</th>
              <th className="w-80">Åtgärder</th>
            </tr>
          </thead>
          <tbody>
            {filteredBatches.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center p-8 text-muted">
                  Inga batcher matchar filtret.
                </td>
              </tr>
            )}
            {filteredBatches.map((b) => (
              <tr key={b.id}>
                <td title={b.name} className="font-medium">{b.name || "(utan namn)"}</td>
                <td>{b.kolliCount ?? 0}</td>
                <td>{fmtDateOnly(b.createdAt)}</td>
                <td>{b.createdBy || "-"}</td>
                <td>
                  <span className="gw-badge">{labelForStatus(b.status)}</span>
                </td>
                <td className="min-w-[200px]">
                  <div className="flex gap-2 flex-wrap">
                    <button
                      type="button"
                      className="gw-btn gw-btn-secondary gw-btn-sm"
                      onClick={() => handleAddKolli(b.id)}
                      title="Skapar Kolli N+1"
                    >
                      + Kolli
                    </button>
                    <button
                      type="button"
                      className="gw-btn gw-btn-secondary gw-btn-sm"
                      onClick={() => goToBatch(b.id)}
                    >
                      Öppna
                    </button>
                    <button
                      type="button"
                      className="gw-btn gw-btn-ghost gw-btn-sm text-red-600 hover:bg-red-50 hover:text-red-700"
                      onClick={() => handleDeleteBatch(b.id)}
                      title="Ta bort batch (endast om inga enheter finns)"
                    >
                      Ta bort
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {openedBatchId && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto">
          {/* Backdrop click to close handled by wrapping div */}
          <div
             className="absolute inset-0"
             onClick={closeBatch}
             aria-hidden="true"
          />

          <div
            className="gw-card relative w-full max-w-[1300px] h-[90vh] flex flex-col p-0 overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <BatchDetail
              db={db}
              batchId={openedBatchId}
              user={user}
              onOpenItem={handleOpenItemFromBatch}
              onClose={closeBatch}
            />
          </div>
        </div>,
        document.body
      )}
    </section>
  );
}
